/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useLayoutEffect, useRef, useState } from "react";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";

import brandLogo from "assets/images/logo.png";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { clearAuth } from "utils/auth";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const collapseName = location.pathname.replace("/", "");
  const brandColor = "rgba(229, 10, 70, 1)";
  const scrollRef = useRef(null);
  const [menuHeight, setMenuHeight] = useState(null);
  const isDesktop = window.innerWidth >= 1200;

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);
  const handleLogout = () => {
    clearAuth();
    navigate("/authentication/sign-in", { replace: true });
  };

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
            data-sidenav-key={key}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            data-sidenav-key={key}
          />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const measure = () => {
      if (window.innerWidth >= 1200) {
        setMenuHeight(null);
        return;
      }

      const target = el.querySelector('[data-sidenav-key="billing"]');
      if (!target) {
        setMenuHeight(null);
        return;
      }

      const containerRect = el.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const computed = Math.ceil(targetRect.bottom - containerRect.top + 8);

      const header = el.previousElementSibling;
      const headerHeight = header ? header.getBoundingClientRect().height : 0;
      const maxAllowed = Math.max(240, Math.floor(window.innerHeight - headerHeight));

      setMenuHeight(Math.min(computed, maxAllowed));
    };

    const raf = requestAnimationFrame(measure);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", measure);
    };
  }, [routes.length, miniSidenav, transparentSidenav, whiteSidenav, darkMode, location.pathname]);

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      sx={{
        "& .MuiDrawer-paper": {
          display: "flex",
          flexDirection: "column",
          top: 0,
          height: "calc(100vh - 32px)",
          maxHeight: "calc(100vh - 32px)",
          marginBottom: "16px",
          overflow: "hidden",
        },
      }}
    >
      <MDBox
        pt={3}
        pb={2}
        px={4}
        textAlign="center"
        sx={{
          borderBottom: `1px solid ${brandColor}`,
        }}
      >
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" sx={{ color: brandColor }}>
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <MDBox
            sx={{
              position: "relative",
              width: "88%",
              aspectRatio: "3.4 / 1",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <MDBox
              component="img"
              src={brandLogo}
              alt={`${brandName} logo`}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                width: "150%",
                height: "auto",
                transform: "translate(-50%, -50%)",
              }}
            />
          </MDBox>
        </MDBox>
      </MDBox>
      <MDBox
        ref={scrollRef}
        sx={{
          flex: menuHeight ? "0 0 auto" : 1,
          height: menuHeight ? `${menuHeight}px` : "auto",
          overflowY: isDesktop ? "visible" : "auto",
          overflowX: "hidden",
          minHeight: 0,
          pt: 2,
          pb: 2,
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(0, 0, 0, 0.18) transparent",
          "&::-webkit-scrollbar": {
            width: "5px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0, 0, 0, 0.18)",
            borderRadius: "8px",
            border: "1px solid transparent",
            backgroundClip: "content-box",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.28)",
          },
        }}
      >
        <List>{renderRoutes}</List>
      </MDBox>
      <MDBox
        px={2}
        py={2}
        mt="auto"
        sx={{
          borderTop: `1px solid ${brandColor}`,
        }}
      >
        <MDButton
          type="button"
          variant="contained"
          fullWidth
          onClick={handleLogout}
          startIcon={<Icon>power_settings_new</Icon>}
          sx={{
            background: "#ffffff",
            color: "rgba(229, 10, 70, 1)",
            justifyContent: "flex-start",
            pl: 1.5,
            "&:hover": {
              background: "#ffffff",
            },
          }}
        >
          Logout
        </MDButton>
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
